/**
 * 오버레이(Drawer/Modal) 백키(뒤로가기) 처리 훅 — React Router 네이티브 구현
 *
 * 문제: 오버레이가 열린 상태에서 백키를 누르면 라우트가 이전 화면으로 이동해버림.
 *
 * 핵심 원칙:
 * 1. window.history를 직접 조작하지 않는다 (React Router v7과 충돌).
 * 2. 마커는 인스턴스별 고유 ID로 소유권을 구분한다 (인스턴스 간 간섭 방지).
 * 3. 오버레이 안의 메뉴 클릭처럼 navigate()와 setOpen(false)가 같은 틱에
 *    발생하는 경합을 흡수한다:
 *    - setState(open=false)가 먼저 커밋 → 마커 엔트리 위에 새 라우트가 push된
 *      뒤 마커 정리가 실행됨. 이때 navigate(-1)을 하면 새 라우트 이동이 취소됨!
 *      → 마커 push "이후" 라우트가 한 번이라도 바뀌었으면(사본 key 비교) back 스킵.
 *
 * 사용법: const [open, setOpen] = useState(false);
 *   useOverlayBackKey(open, () => setOpen(false));
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const MARKER = "__overlayId";

export const useOverlayBackKey = (open: boolean, onClose: () => void) => {
  const location = useLocation();
  const navigate = useNavigate();

  /** 이 인스턴스의 고유 마커 ID */
  const idRef = useRef(`ovl_${Math.random().toString(36).slice(2)}`);
  /** 내 마커 엔트리를 push 했는지 */
  const pushedRef = useRef(false);
  /** 마커 push 시점의 location.key 사본 — 이후 라우트 변경 감지용 */
  const pushedKeyRef = useRef<string>("");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const state = location.state as Record<string, unknown> | null | undefined;
    const mine = !!state && state[MARKER] === idRef.current;

    if (open) {
      if (!pushedRef.current) {
        // 오버레이 열림 → 내 마커 엔트리 push (라우터 경유, URL 불변)
        pushedRef.current = true;
        pushedKeyRef.current = location.key;
        navigate(
          { pathname: location.pathname, search: location.search, hash: location.hash },
          { state: { ...(state as object), [MARKER]: idRef.current }, replace: false },
        );
      } else if (!mine) {
        // 백키로 내 마커 엔트리를 빠져나감 → 오버레이만 닫기 (history 조작 없음)
        pushedRef.current = false;
        onCloseRef.current();
      }
    } else if (pushedRef.current) {
      pushedRef.current = false;
      // 마커 push 이후 라우트가 그대로(마커 엔트리 그 자리)일 때만 back —
      // 메뉴 클릭 등으로 새 라우트가 push된 뒤라면 back()이 그 이동을 취소하므로 스킵.
      if (mine && location.key === pushedKeyRef.current) {
        navigate(-1);
      }
    }
    // (open=false && !pushed && mine) 재도착 정리 분기 제거 —
    // 인스턴스 ID 도입 후 다른 인스턴스가 이 마커를 함부로 지우지 못하므로
    // 마커가 남아도 오탐(재오픈)이 발생하지 않음. state는 세션 내에서만 유효.
  }, [open, location.key, location.state, location.pathname, location.search, location.hash, navigate]);
};
